import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (startDate: Date | null, endDate: Date | null) => void;
  onPresetSelect?: (preset: string) => void;
  onReset?: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  onPresetSelect,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatDateRange = (start: Date | null, end: Date | null): string => {
    if (!start && !end) return 'Select date range';
    if (!start) return 'Start date...';
    if (!end) return `${formatDate(start)} - End date...`;
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const formatDate = (date: Date): string => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month} ${year}`;
  };

  const handlePresetClick = (preset: string) => {
    if (onPresetSelect) {
      onPresetSelect(preset);
    }
    setIsOpen(false);
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
    setIsOpen(false);
  };

  const applyPreset = (preset: string) => {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    const startOfWeek = (d: Date) => {
      const day = d.getDay();
      const diff = (day === 0 ? -6 : 1) - day;
      const s = new Date(d);
      s.setDate(d.getDate() + diff);
      return startOfDay(s);
    };
    const endOfWeek = (d: Date) => {
      const s = startOfWeek(d);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return endOfDay(e);
    };
    const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
    const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    const startOfQuarter = (d: Date) => {
      const quarter = Math.floor(d.getMonth() / 3);
      return new Date(d.getFullYear(), quarter * 3, 1, 0, 0, 0);
    };
    const endOfQuarter = (d: Date) => {
      const quarter = Math.floor(d.getMonth() / 3);
      return new Date(d.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999);
    };

    let s: Date | null = null;
    let e: Date | null = null;

    switch (preset) {
      case 'today':
        s = startOfDay(now);
        e = now;
        break;
      case 'yesterday': {
        const y = new Date(now);
        y.setDate(now.getDate() - 1);
        s = startOfDay(y);
        e = endOfDay(y);
        break;
      }
      case 'lastWeek': {
        const lwEnd = new Date(startOfWeek(now));
        lwEnd.setDate(lwEnd.getDate() - 1);
        const lwStart = new Date(lwEnd);
        lwStart.setDate(lwEnd.getDate() - 6);
        s = startOfDay(lwStart);
        e = endOfDay(lwEnd);
        break;
      }
      case 'lastMonth': {
        const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        s = startOfMonth(lm);
        e = endOfMonth(lm);
        break;
      }
      case 'lastQuarter': {
        // Get the previous quarter
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const lastQuarterMonth = currentQuarter === 0 ? 9 : (currentQuarter - 1) * 3;
        const lastQuarterYear = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const lq = new Date(lastQuarterYear, lastQuarterMonth, 1);
        s = startOfQuarter(lq);
        e = endOfQuarter(lq);
        break;
      }
    }

    if (s && e) {
      onChange(s, e);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Input field */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 text-left border border-blue-300 rounded-md bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
      >
        <span className="text-sm text-gray-700">
          {formatDateRange(startDate, endDate)}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="flex">
            {/* Left panel - Presets */}
            <div className="w-48 border-r border-gray-200 bg-gray-50">
              <div className="p-2">
                <button
                  onClick={() => {
                    applyPreset('today');
                    handlePresetClick('today');
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded"
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    applyPreset('yesterday');
                    handlePresetClick('yesterday');
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded"
                >
                  Yesterday
                </button>
                <button
                  onClick={() => {
                    applyPreset('lastWeek');
                    handlePresetClick('lastWeek');
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded"
                >
                  Last week
                </button>
                <button
                  onClick={() => {
                    applyPreset('lastMonth');
                    handlePresetClick('lastMonth');
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded"
                >
                  Last month
                </button>
                <button
                  onClick={() => {
                    applyPreset('lastQuarter');
                    handlePresetClick('lastQuarter');
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded"
                >
                  Last quarter
                </button>
              </div>
              <div className="border-t border-gray-200 p-2">
                <button
                  onClick={handleReset}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Right panel - Calendar */}
            <div className="p-4">
              <DatePicker
                selected={startDate}
                onChange={(dates) => {
                  const [start, end] = dates as [Date | null, Date | null];
                  // Only call onChange if dates actually changed
                  const startChanged = start?.getTime() !== startDate?.getTime();
                  const endChanged = end?.getTime() !== endDate?.getTime();
                  if (startChanged || endChanged) {
                    onChange(start, end);
                  }
                  if (start && end) {
                    setIsOpen(false);
                  }
                }}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
